import { BulkConfirmation } from '../bulk/types';
import { matchesSearch } from '../core/search';

export type MessageDirection = 'INBOUND' | 'OUTBOUND';

export type MessageType =
  | 'TEXT'
  | 'TEMPLATE'
  | 'IMAGE'
  | 'DOCUMENT'
  | 'VIDEO'
  | 'AUDIO'
  | 'STICKER'
  | 'CONTACTS'
  | 'REACTION';

export type MessageStatus = 'ACCEPTED' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

export interface MessageMedia {
  mediaId: string;
  mimeType?: string;
  fileName?: string;
  caption?: string;
  sha256?: string;
  voice?: boolean;
}

export interface MessageSticker {
  mediaId: string;
  mimeType?: string;
  sha256?: string;
  animated?: boolean;
}

export interface MessageReaction {
  messageId: string;
  emoji?: string;
}

export interface MessageReactionEntry {
  emoji: string;
  direction?: MessageDirection;
  reactedAt?: string;
}

export interface MessageContactPhone {
  phone?: string;
  type?: string;
  waId?: string;
}

export interface MessageContactEmail {
  email?: string;
  type?: string;
}

export interface MessageContactUrl {
  url?: string;
  type?: string;
}

export interface MessageContactAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
  type?: string;
}

export interface MessageContactOrg {
  company?: string;
  department?: string;
  title?: string;
}

export interface MessageContact {
  formattedName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phones?: MessageContactPhone[];
  emails?: MessageContactEmail[];
  urls?: MessageContactUrl[];
  addresses?: MessageContactAddress[];
  org?: MessageContactOrg;
  birthday?: string;
}

export interface MessageTemplateButton {
  type?: string;
  text?: string;
  url?: string;
  phoneNumber?: string;
}

export interface MessageTemplateSnapshot {
  category?: string;
  headerFormat?: string;
  header?: string;
  body?: string;
  bodyRendered?: string;
  footer?: string;
  buttons?: MessageTemplateButton[];
}

export interface MessageContent {
  text?: string;
  templateName?: string;
  templateLanguage?: string;
  templateVariables?: Record<string, string>;
  template?: MessageTemplateSnapshot;
  media?: MessageMedia;
  sticker?: MessageSticker;
  contacts?: MessageContact[];
  reaction?: MessageReaction;
}

export interface TemplateVariableValue {
  token: string;
  value: string;
}

export function templateVariableValues(content: MessageContent): TemplateVariableValue[] {
  return Object.entries(content.templateVariables ?? {})
    .map(([index, value]) => ({ token: `{{${index}}}`, value }));
}

export function contactDisplayName(contact: MessageContact): string {
  const formattedName = contact.formattedName?.trim();

  if (formattedName) {
    return formattedName;
  }

  const composedName = [contact.firstName, contact.middleName, contact.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');

  return composedName || contact.phones?.find(entry => entry.phone?.trim())?.phone?.trim() || '';
}

export interface Message {
  id: string;
  wamid?: string;
  direction: MessageDirection;
  type: MessageType;
  content: MessageContent;
  reactions?: MessageReactionEntry[];
  status?: MessageStatus;
  failReason?: string | null;
  messageAt: string;
  pending?: boolean;
  retryable?: boolean;
}

export type MessagePatch = Partial<Message> & { id: string };

export interface Conversation {
  phoneNumber: string;
  name: string | null;
  chosenName?: string | null;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastMessageDirection: MessageDirection;
  lastInboundAt?: string | null;
  messageWindowCloseAt?: string | null;
  unreadCount?: number;
}

export function unreadCountOf(conversation: Conversation | undefined): number {
  return conversation?.unreadCount ?? 0;
}

export function withUnreadCount(conversation: Conversation, unreadCount: number): Conversation {
  return { ...conversation, unreadCount: Math.max(0, unreadCount) };
}

export function conversationName(conversation: Conversation | undefined): string | null {
  return conversation?.chosenName || conversation?.name || null;
}

export function conversationMatchesSearch(conversation: Conversation, query: string): boolean {
  const trimmed = query.trim();

  if (!trimmed) {
    return true;
  }

  const digits = trimmed.replace(/\D/g, '');

  if (digits && !/\p{L}/u.test(trimmed)) {
    return conversation.phoneNumber.replace(/\D/g, '').includes(digits);
  }

  return matchesSearch(trimmed, [conversation.chosenName, conversation.name]);
}

export interface MarkConversationAsReadProps {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
}

export interface MarkedAsRead {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
  markedCount: number;
  readAt: string | null;
}

export interface UpdateContactNameProps {
  clientPhoneNumber: string;
  chosenName: string;
}

export interface ContactName {
  phoneNumber: string;
  name: string | null;
  chosenName: string | null;
}

export const MAX_CONTACT_NAME_LENGTH = 120;

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

export interface ListConversationsOptions {
  companyPhoneNumber: string;
  cursor?: string | null;
  limit?: number;
}

export interface ListMessagesOptions {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
  cursor?: string | null;
  limit?: number;
}

export interface SendTextMessage {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
  type: 'TEXT';
  content: { text: string };
}

export interface SendTemplateMessage {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
  type: 'TEMPLATE';
  content: {
    templateName: string;
    language: string;
    variables: Record<string, string>;
  };
  confirmation?: BulkConfirmation;
}

export interface SendMediaMessage {
  companyPhoneNumber: string;
  clientPhoneNumber: string;
  type: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO';
  content: {
    mediaId: string;
    fileName?: string;
    caption?: string;
  };
}

export type SendMessageProps = SendTextMessage | SendTemplateMessage | SendMediaMessage;

export interface SentMessage {
  id: string;
  wamid: string;
  status: MessageStatus;
}

export const CONVERSATION_WINDOW_HOURS = 24;

const CONVERSATION_WINDOW_MS = CONVERSATION_WINDOW_HOURS * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

export const MEDIA_RETENTION_DAYS = 7;

const MEDIA_MESSAGE_TYPES: MessageType[] = ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'STICKER'];

export function isMediaMessage(message: Message): boolean {
  return MEDIA_MESSAGE_TYPES.includes(message.type);
}

export function mediaExpiresAt(message: Message): Date | null {
  if (!isMediaMessage(message)) {
    return null;
  }

  const messageAt = new Date(message.messageAt);

  if (Number.isNaN(messageAt.getTime())) {
    return null;
  }

  return new Date(messageAt.getTime() + MEDIA_RETENTION_DAYS * DAY_MS);
}

export function isWindowOpenFromMessages(messages: Message[], now: Date = new Date()): boolean {
  const closeAt = windowCloseAtFromMessages(messages);

  return !!closeAt && isWindowOpenAt(closeAt, now);
}

export function windowCloseAtFromMessages(messages: Message[]): Date | null {
  const lastInbound = [...messages].reverse().find(message => message.direction === 'INBOUND');

  if (!lastInbound) {
    return null;
  }

  const messageAt = new Date(lastInbound.messageAt);

  if (Number.isNaN(messageAt.getTime())) {
    return null;
  }

  return new Date(messageAt.getTime() + CONVERSATION_WINDOW_MS);
}

export type ConversationWindowState = 'unknown' | 'open' | 'closed';

export interface ConversationWindowStatus {
  state: ConversationWindowState;
  closeAt: Date | null;
}

export function conversationWindowStatus(
  conversation: Conversation,
  now: Date = new Date()
): ConversationWindowStatus {
  if (conversation.messageWindowCloseAt !== undefined) {
    if (conversation.messageWindowCloseAt === null) {
      return { state: 'unknown', closeAt: null };
    }

    const closeAt = new Date(conversation.messageWindowCloseAt);

    if (Number.isNaN(closeAt.getTime())) {
      return { state: 'unknown', closeAt: null };
    }

    return { state: now.getTime() < closeAt.getTime() ? 'open' : 'closed', closeAt };
  }

  const inboundAt = conversation.lastInboundAt
    ? conversation.lastInboundAt
    : conversation.lastMessageDirection === 'INBOUND'
      ? conversation.lastMessageAt
      : null;

  if (!inboundAt) {
    return { state: 'unknown', closeAt: null };
  }

  const inbound = new Date(inboundAt);

  if (Number.isNaN(inbound.getTime())) {
    return { state: 'unknown', closeAt: null };
  }

  const closeAt = new Date(inbound.getTime() + CONVERSATION_WINDOW_MS);

  return { state: isWindowOpenAt(closeAt, now) ? 'open' : 'closed', closeAt };
}

export function isWindowOpenFromConversation(
  conversation: Conversation,
  now: Date = new Date()
): boolean {
  return conversationWindowStatus(conversation, now).state === 'open';
}

function isWindowOpenAt(closeAt: Date, now: Date): boolean {
  const remainingMs = closeAt.getTime() - now.getTime();

  return remainingMs > 0 && remainingMs <= CONVERSATION_WINDOW_MS;
}

export function buildMessagePreview(message: Message): string {
  if (message.type === 'TEXT') {
    return message.content.text ?? '';
  }

  if (message.type === 'TEMPLATE') {
    return message.content.text || '[Template]';
  }

  if (message.type === 'STICKER') {
    return '[Figurinha]';
  }

  if (message.type === 'CONTACTS') {
    const contacts = message.content.contacts ?? [];

    if (contacts.length > 1) {
      return `[${contacts.length} contatos]`;
    }

    const name = contacts[0] ? contactDisplayName(contacts[0]) : '';
    return name ? `[Contato] ${name}` : '[Contato]';
  }

  if (message.type === 'REACTION') {
    const emoji = message.content.reaction?.emoji;
    return emoji ? `Reagiu com ${emoji}` : 'Reação removida';
  }

  return message.content.media?.caption || `[${mediaTypeLabel(message.type)}]`;
}

export function chatMessages(messages: Message[]): Message[] {
  return messages.filter(message => message.type !== 'REACTION');
}

export const UNKNOWN_DAY_KEY = 'unknown';

export interface MessageDayGroup {
  key: string;
  date: Date | null;
  messages: Message[];
}

export function groupMessagesByDay(messages: Message[]): MessageDayGroup[] {
  const groups = new Map<string, MessageDayGroup>();

  for (const message of messages) {
    const messageAt = new Date(message.messageAt);
    const isUsable = !Number.isNaN(messageAt.getTime());
    const key = isUsable ? dayKeyOf(messageAt) : UNKNOWN_DAY_KEY;
    const group = groups.get(key);

    if (group) {
      group.messages.push(message);
      continue;
    }

    groups.set(key, {
      key,
      date: isUsable ? startOfDayOf(messageAt) : null,
      messages: [message],
    });
  }

  return [...groups.values()];
}

function dayKeyOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

function startOfDayOf(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const MEDIA_TYPE_LABELS: Record<string, string> = {
  IMAGE: 'Imagem',
  VIDEO: 'Vídeo',
  AUDIO: 'Áudio',
  DOCUMENT: 'Documento',
  STICKER: 'Figurinha',
};

export function mediaTypeLabel(type: MessageType): string {
  return MEDIA_TYPE_LABELS[type] ?? 'Anexo';
}

export function mediaMessageTypeOf(mimeType: string): 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO' {
  if (mimeType.startsWith('image/')) {
    return 'IMAGE';
  }

  if (mimeType.startsWith('video/')) {
    return 'VIDEO';
  }

  if (mimeType.startsWith('audio/')) {
    return 'AUDIO';
  }

  return 'DOCUMENT';
}

export interface MessageDraft {
  type: MessageType;
  content: MessageContent;
}

const LOCAL_MESSAGE_PREFIX = 'local:';

let localMessageCounter = 0;

export function buildPendingMessage(draft: MessageDraft, now: Date = new Date()): Message {
  localMessageCounter += 1;

  return {
    id: `${LOCAL_MESSAGE_PREFIX}${now.getTime()}-${localMessageCounter}`,
    direction: 'OUTBOUND',
    type: draft.type,
    content: draft.content,
    messageAt: now.toISOString(),
    pending: true,
  };
}

export function draftOfSendProps(props: SendMessageProps): MessageDraft {
  if (props.type === 'TEXT') {
    return { type: 'TEXT', content: { text: props.content.text } };
  }

  if (props.type === 'TEMPLATE') {
    return {
      type: 'TEMPLATE',
      content: {
        templateName: props.content.templateName,
        templateLanguage: props.content.language,
        templateVariables: props.content.variables,
      },
    };
  }

  return {
    type: props.type,
    content: {
      media: {
        mediaId: props.content.mediaId,
        fileName: props.content.fileName,
        caption: props.content.caption,
      },
    },
  };
}
